import React from "react";
import { Agent, TerminalTheme } from "../types";
import { Network, Layers } from "lucide-react";

interface AgentSwitcherProps {
  agents: Agent[];
  selectedAgentId: string | null;
  onSelect: (id: string | null) => void;
  theme: TerminalTheme;
}

/**
 * Compact dropdown for picking the scope agent. Single <select> with an
 * "All agents (Fleet Overview)" option at the top — switched from the
 * stacked-button list on 2026-05-24 per Terrell ("cleaner look and feel").
 *
 * Status dot (right side) follows the currently-selected agent so the
 * operator always sees its state at a glance without expanding the menu.
 *
 * Single-agent mode keeps the same tiny hint card.
 */
export const AgentSwitcher: React.FC<AgentSwitcherProps> = ({
  agents,
  selectedAgentId,
  onSelect,
  theme,
}) => {
  if (agents.length <= 1) {
    return (
      <div
        style={{ backgroundColor: theme.inputBg, borderColor: theme.border }}
        className="border p-3 font-mono select-none rounded"
        id="agent-switcher-single"
      >
        <div className="flex items-center gap-2 text-[10px]">
          <Layers size={12} style={{ color: theme.accent }} />
          <span style={{ color: theme.systemText }} className="uppercase tracking-wider">
            single-agent mode
          </span>
        </div>
      </div>
    );
  }

  const current = selectedAgentId
    ? agents.find((a) => a.id === selectedAgentId)
    : null;
  const status = current?.status;
  const dotColor =
    status === "online"
      ? "#10b981"
      : status === "busy"
        ? "#f59e0b"
        : status === "rebooting"
          ? "#a78bfa"
          : status === "offline"
            ? "#52525b"
            : "#f59e0b"; // fleet-overview = amber (read-only state)

  return (
    <div
      style={{ backgroundColor: theme.inputBg, borderColor: theme.border }}
      className="border p-3 font-mono select-none rounded space-y-2"
      id="agent-switcher-panel"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Network size={12} style={{ color: theme.accent }} />
          <span style={{ color: theme.systemText }} className="text-[10px] font-bold uppercase tracking-wider">
            Fleet
          </span>
        </div>
        <span style={{ color: theme.systemText }} className="text-[10px]">
          {agents.length} agent{agents.length === 1 ? "" : "s"}
        </span>
      </div>

      <div
        style={{ backgroundColor: theme.bg, borderColor: theme.border }}
        className="flex items-center gap-2 border rounded px-2.5 py-1.5"
      >
        <span
          className="h-2 w-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: dotColor }}
          title={current ? `${current.name}: ${status || "unknown"}` : "Fleet Overview · read-only"}
        />
        <select
          value={selectedAgentId ?? "__all__"}
          onChange={(e) => {
            const v = e.target.value;
            onSelect(v === "__all__" ? null : v);
          }}
          style={{ color: theme.fg }}
          className="flex-1 bg-transparent border-none text-[11px] font-bold focus:ring-0 focus:outline-none cursor-pointer truncate"
          aria-label="Scope agent"
        >
          <option value="__all__" style={{ backgroundColor: theme.bg, color: theme.fg }}>
            All agents · read-only
          </option>
          {agents.map((agent) => (
            <option
              key={agent.id}
              value={agent.id}
              style={{ backgroundColor: theme.bg, color: theme.fg }}
            >
              {agent.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
